import { useEffect, useMemo, useState } from "react";
import { Calculator, Package, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import http from "../api/http";
import { guardarBorradorProducto } from "../utils/navigationIntent";

const unidadesPorTipo = {
  Peso: ["mg", "g", "kg"],
  Volumen: ["ml", "l"],
};

const unidadesCosto = {
  mg: { tipo: "Peso", factor: 0.000001 },
  g: { tipo: "Peso", factor: 0.001 },
  kg: { tipo: "Peso", factor: 1 },
  ml: { tipo: "Volumen", factor: 0.001 },
  l: { tipo: "Volumen", factor: 1 },
};

const ingredienteVacio = () => ({
  productoId: "",
  producto: "",
  tipo: "",
  tipoMedida: "Peso",
  cantidad: "",
  unidadMedida: "kg",
});

const materialVacio = () => ({ materialId: "", nombre: "", cantidad: "" });
const presentacionVacia = (tipoMedida) => ({
  cantidad: "",
  unidadMedida: tipoMedida === "Volumen" ? "ml" : "g",
  precioVenta: "",
  origenGeneral: false,
});
const presentacionesDeTipo = (presentaciones, tipoMedida) => (presentaciones || [])
  .filter((item) => item.tipoMedida === tipoMedida)
  .map((item) => ({
    cantidad: item.cantidad,
    unidadMedida: item.unidadMedida,
    precioVenta: "",
    origenGeneral: true,
  }));
const normalizar = (value) => String(value || "").trim().toLowerCase();
const idReferencia = (value) => String(typeof value === "object" ? value?._id || "" : value || "");
const unidadesDe = (tipo) => unidadesPorTipo[tipo] || unidadesPorTipo.Peso;
const baseDe = (tipo) => tipo === "Volumen" ? "l" : "kg";
const COTIZACION_DRAFT_KEY = "lainternacional:cotizacion:borrador:v1";

const recuperarBorrador = () => {
  try {
    const data = JSON.parse(window.localStorage.getItem(COTIZACION_DRAFT_KEY) || "null");
    return data && typeof data === "object" ? data : null;
  } catch {
    return null;
  }
};

const guardarBorrador = (data) => {
  try {
    window.localStorage.setItem(COTIZACION_DRAFT_KEY, JSON.stringify(data));
  } catch {
    // El formulario sigue funcionando aunque el navegador bloquee el almacenamiento local.
  }
};

const convertirUnidad = (cantidad, origen, destino) => {
  const unidadOrigen = unidadesCosto[normalizar(origen)];
  const unidadDestino = unidadesCosto[normalizar(destino)];
  if (!unidadOrigen || !unidadDestino || unidadOrigen.tipo !== unidadDestino.tipo) return null;
  return (Number(cantidad || 0) * unidadOrigen.factor) / unidadDestino.factor;
};

const formatearImporte = (value) => Number(value || 0).toLocaleString("es-AR", {
  style: "currency",
  currency: "ARS",
});

const formatearCantidad = (value) => Number(value || 0).toLocaleString("es-AR", {
  maximumFractionDigits: 6,
});

export default function Cotizaciones() {
  const navigate = useNavigate();
  const [borradorInicial] = useState(recuperarBorrador);
  const [productos, setProductos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [presentacionesGenerales, setPresentacionesGenerales] = useState([]);
  const [presentaciones, setPresentaciones] = useState(() =>
    Array.isArray(borradorInicial?.presentaciones) ? borradorInicial.presentaciones : []);
  const [nombre, setNombre] = useState(() => String(borradorInicial?.nombre || ""));
  const [tipoMedida, setTipoMedida] = useState(() =>
    borradorInicial?.tipoMedida === "Volumen" ? "Volumen" : "Peso");
  const [unidadMedida, setUnidadMedida] = useState(() =>
    String(borradorInicial?.unidadMedida || baseDe(borradorInicial?.tipoMedida)));
  const [rendimiento, setRendimiento] = useState(() => String(borradorInicial?.rendimiento ?? "1"));
  const [margen, setMargen] = useState(() => String(borradorInicial?.margen ?? "0"));
  const [receta, setReceta] = useState(() =>
    Array.isArray(borradorInicial?.receta) ? borradorInicial.receta : [ingredienteVacio()]);
  const [materialesReceta, setMaterialesReceta] = useState(() =>
    Array.isArray(borradorInicial?.materialesReceta) ? borradorInicial.materialesReceta : []);
  const [seleccionesProveedor, setSeleccionesProveedor] = useState(() =>
    borradorInicial?.seleccionesProveedor && typeof borradorInicial.seleccionesProveedor === "object"
      ? borradorInicial.seleccionesProveedor
      : {});
  const [versionCotizacion, setVersionCotizacion] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let activo = true;
    Promise.all([
      http.get("/productos"),
      http.get("/proveedores"),
      http.get("/materiales-produccion"),
      http.get("/productos/presentaciones-generales"),
    ])
      .then(([productosRes, proveedoresRes, materialesRes, presentacionesRes]) => {
        if (!activo) return;
        const generales = Array.isArray(presentacionesRes.data) ? presentacionesRes.data : [];
        setProductos(Array.isArray(productosRes.data) ? productosRes.data : []);
        setProveedores(Array.isArray(proveedoresRes.data) ? proveedoresRes.data : []);
        setMateriales(Array.isArray(materialesRes.data) ? materialesRes.data : []);
        setPresentacionesGenerales(generales);
        if (!borradorInicial) setPresentaciones(presentacionesDeTipo(generales, "Peso"));
      })
      .catch((err) => {
        if (activo) setError(err.response?.data?.error || "No se pudieron cargar los datos para cotizar.");
      })
      .finally(() => {
        if (activo) setCargando(false);
      });
    return () => {
      activo = false;
    };
  }, [borradorInicial]);

  useEffect(() => {
    guardarBorrador({
      nombre,
      tipoMedida,
      unidadMedida,
      rendimiento,
      margen,
      receta,
      materialesReceta,
      seleccionesProveedor,
      presentaciones,
    });
  }, [
    margen,
    materialesReceta,
    nombre,
    presentaciones,
    receta,
    rendimiento,
    seleccionesProveedor,
    tipoMedida,
    unidadMedida,
  ]);

  const ingredientesDisponibles = useMemo(
    () => productos.filter((producto) =>
      producto.activo !== false && ["Materia prima", "Intermedio"].includes(producto.tipo)),
    [productos],
  );

  const actualizarIngrediente = (index, name, value) => {
    setReceta((actual) => actual.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      if (name !== "productoId") return { ...item, [name]: value };
      const producto = ingredientesDisponibles.find((opcion) => String(opcion._id) === String(value));
      if (!producto) return ingredienteVacio();
      return {
        ...item,
        productoId: producto._id,
        producto: producto.nombre,
        tipo: producto.tipo,
        tipoMedida: producto.tipoMedida || "Peso",
        unidadMedida: producto.unidadMedida || baseDe(producto.tipoMedida),
      };
    }));
    setSeleccionesProveedor({});
  };

  const actualizarMaterial = (index, name, value) => {
    setMaterialesReceta((actual) => actual.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      if (name !== "materialId") return { ...item, [name]: value };
      const material = materiales.find((opcion) => String(opcion._id) === String(value));
      return material
        ? { ...item, materialId: material._id, nombre: material.nombre }
        : materialVacio();
    }));
    setSeleccionesProveedor({});
  };

  const simulacion = useMemo(() => {
    const productosPorId = new Map(productos.map((item) => [String(item._id), item]));
    const productosPorNombre = new Map(productos.map((item) => [normalizar(item.nombre), item]));
    const componentes = new Map();
    const errores = new Set();

    const agregar = ({ tipo, id, nombre: nombreComponente, cantidad, unidad }) => {
      const clave = `${tipo}:${id || normalizar(nombreComponente)}`;
      const existente = componentes.get(clave);
      if (existente) existente.cantidad += Number(cantidad || 0);
      else componentes.set(clave, {
        clave,
        tipo,
        id,
        nombre: nombreComponente,
        cantidad: Number(cantidad || 0),
        unidad,
      });
    };

    const expandirProducto = (producto, cantidadRecetas, recorrido = []) => {
      const productoId = String(producto?._id || normalizar(producto?.nombre));
      if (recorrido.includes(productoId)) {
        errores.add(`Referencia circular en la receta de ${producto?.nombre || "un producto"}.`);
        return;
      }
      (producto.materialesProduccion || []).forEach((material) => agregar({
        tipo: "material",
        id: idReferencia(material.materialId),
        nombre: material.nombre,
        cantidad: Number(material.cantidad || 0) * cantidadRecetas,
        unidad: "unidad",
      }));
      (producto.receta || []).forEach((ingrediente) => {
        const cantidad = Number(ingrediente.cantidad || 0) * cantidadRecetas;
        if (ingrediente.tipo === "Materia prima") {
          const base = baseDe(ingrediente.tipoMedida);
          const cantidadBase = convertirUnidad(cantidad, ingrediente.unidadMedida, base);
          if (cantidadBase === null) errores.add(`No se pudo convertir la unidad de ${ingrediente.producto}.`);
          else agregar({
            tipo: "ingrediente",
            id: idReferencia(ingrediente.productoId),
            nombre: ingrediente.producto,
            cantidad: cantidadBase,
            unidad: base,
          });
          return;
        }
        const intermedio = productosPorId.get(idReferencia(ingrediente.productoId)) ||
          productosPorNombre.get(normalizar(ingrediente.producto));
        const rendimientoIntermedio = intermedio?.recetaRendimiento;
        const cantidadIntermedia = intermedio
          ? convertirUnidad(cantidad, ingrediente.unidadMedida, rendimientoIntermedio?.unidadMedida)
          : null;
        if (!intermedio || cantidadIntermedia === null || Number(rendimientoIntermedio?.cantidad || 0) <= 0) {
          errores.add(`No se pudo calcular el intermedio ${ingrediente.producto}.`);
          return;
        }
        expandirProducto(
          intermedio,
          cantidadIntermedia / Number(rendimientoIntermedio.cantidad),
          [...recorrido, productoId],
        );
      });
    };

    receta.forEach((ingrediente) => {
      if (!ingrediente.productoId) return;
      if (Number(ingrediente.cantidad || 0) <= 0) {
        errores.add(`Indica una cantidad valida para ${ingrediente.producto}.`);
        return;
      }
      if (ingrediente.tipo === "Materia prima") {
        const base = baseDe(ingrediente.tipoMedida);
        const cantidadBase = convertirUnidad(ingrediente.cantidad, ingrediente.unidadMedida, base);
        if (cantidadBase === null) errores.add(`No se pudo convertir la unidad de ${ingrediente.producto}.`);
        else agregar({
          tipo: "ingrediente",
          id: idReferencia(ingrediente.productoId),
          nombre: ingrediente.producto,
          cantidad: cantidadBase,
          unidad: base,
        });
        return;
      }
      const intermedio = productosPorId.get(idReferencia(ingrediente.productoId));
      const rendimientoIntermedio = intermedio?.recetaRendimiento;
      const cantidadIntermedia = intermedio
        ? convertirUnidad(ingrediente.cantidad, ingrediente.unidadMedida, rendimientoIntermedio?.unidadMedida)
        : null;
      if (!intermedio || cantidadIntermedia === null || Number(rendimientoIntermedio?.cantidad || 0) <= 0) {
        errores.add(`No se pudo calcular el intermedio ${ingrediente.producto}.`);
      } else {
        expandirProducto(intermedio, cantidadIntermedia / Number(rendimientoIntermedio.cantidad));
      }
    });

    materialesReceta.forEach((material) => {
      if (!material.materialId) return;
      if (Number(material.cantidad || 0) <= 0) {
        errores.add(`Indica una cantidad valida para ${material.nombre}.`);
        return;
      }
      agregar({
        tipo: "material",
        id: idReferencia(material.materialId),
        nombre: material.nombre,
        cantidad: Number(material.cantidad),
        unidad: "unidad",
      });
    });

    const lineas = [...componentes.values()].map((componente) => {
      const opciones = proveedores.map((proveedor) => {
        const asociado = componente.tipo === "material"
          ? (proveedor.materiales || []).find((item) => item.activo !== false &&
              ((componente.id && idReferencia(item.materialProduccionId) === componente.id) ||
                normalizar(item.nombre) === normalizar(componente.nombre)))
          : (proveedor.productos || []).find((item) => item.activo !== false &&
              ((componente.id && idReferencia(item.productoCatalogoId) === componente.id) ||
                normalizar(item.nombre) === normalizar(componente.nombre)));
        if (!asociado) return null;
        return {
          proveedor,
          precio: Number(asociado.precioCompraPorUnidad ?? asociado.precioPorKg ?? 0),
        };
      }).filter(Boolean).sort((a, b) => {
        if (a.precio <= 0 && b.precio > 0) return 1;
        if (b.precio <= 0 && a.precio > 0) return -1;
        return a.precio - b.precio;
      });
      const seleccionada = opciones.find((opcion) =>
        String(opcion.proveedor._id) === String(seleccionesProveedor[componente.clave])) || opciones[0] || null;
      return {
        ...componente,
        opciones,
        seleccionada,
        costoTotal: componente.cantidad * Number(seleccionada?.precio || 0),
      };
    });

    const costoIngredientes = lineas.filter((linea) => linea.tipo === "ingrediente")
      .reduce((total, linea) => total + linea.costoTotal, 0);
    const costoMateriales = lineas.filter((linea) => linea.tipo === "material")
      .reduce((total, linea) => total + linea.costoTotal, 0);
    const costoTotal = costoIngredientes + costoMateriales;
    const rendimientoBase = convertirUnidad(rendimiento, unidadMedida, baseDe(tipoMedida));
    if (!rendimientoBase || rendimientoBase <= 0) errores.add("Indica un rendimiento valido para la receta.");
    const costoPorUnidad = rendimientoBase > 0 ? costoTotal / rendimientoBase : 0;
    const porcentaje = Math.max(Number(margen || 0), 0);

    return {
      lineas,
      errores: [...errores],
      faltantes: lineas.filter((linea) => !linea.seleccionada || linea.seleccionada.precio <= 0),
      costoIngredientes,
      costoMateriales,
      costoTotal,
      costoPorUnidad,
      precioVentaTotal: costoTotal * (1 + porcentaje / 100),
      precioVentaPorUnidad: costoPorUnidad * (1 + porcentaje / 100),
      rendimientoBase: rendimientoBase || 0,
    };
  }, [margen, materialesReceta, productos, proveedores, receta, rendimiento, seleccionesProveedor, tipoMedida, unidadMedida]);

  const recetaValida = receta.some((item) => item.productoId) && receta.every((item) =>
    item.productoId && Number(item.cantidad || 0) > 0);
  const materialesValidos = materialesReceta.every((item) =>
    item.materialId && Number(item.cantidad || 0) > 0);
  const precioSugeridoPresentacion = (item) => Math.round(
    Number(convertirUnidad(item.cantidad, item.unidadMedida, baseDe(tipoMedida)) || 0) *
      Number(simulacion.precioVentaPorUnidad || 0) * 100,
  ) / 100;
  const actualizarPresentacion = (index, name, value) => setPresentaciones((actual) => actual.map(
    (item, itemIndex) => itemIndex === index ? { ...item, [name]: value } : item,
  ));
  const presentacionesValidas = presentaciones.every((item) =>
    Number(item.cantidad || 0) > 0 && Number(item.precioVenta || precioSugeridoPresentacion(item)) > 0);
  const puedeCrear = nombre.trim() && Number(rendimiento || 0) > 0 && recetaValida && materialesValidos && presentacionesValidas &&
    simulacion.errores.length === 0;

  const crearProducto = () => {
    if (!puedeCrear) return;
    guardarBorradorProducto({
      nombre: nombre.trim(),
      tipo: "Final",
      tipoMedida,
      unidadMedida,
      stockMinimo: "",
      recetaRendimiento: {
        tipoMedida,
        cantidad: Number(rendimiento),
        unidadMedida,
      },
      margenGananciaPorcentaje: Math.max(Number(margen || 0), 0),
      costoCalculadoPorUnidad: Number(simulacion.costoPorUnidad || 0),
      precioVentaPorUnidad: Number(simulacion.precioVentaPorUnidad || 0),
      receta: receta.map((item) => ({ ...item, cantidad: Number(item.cantidad) })),
      materialesProduccion: materialesReceta.map((item) => ({ ...item, cantidad: Number(item.cantidad) })),
      presentacionesFraccionamiento: presentaciones.map((item) => ({
        cantidad: Number(item.cantidad),
        unidadMedida: item.unidadMedida,
        precioVenta: Number(item.precioVenta || precioSugeridoPresentacion(item)),
        origenGeneral: item.origenGeneral === true,
      })),
      activo: true,
    });
    navigate("/productos?nuevo=1");
  };

  const limpiarCotizacion = () => {
    if (!window.confirm("Limpiar todos los datos de la cotizacion actual?")) return;
    window.localStorage.removeItem(COTIZACION_DRAFT_KEY);
    setNombre("");
    setTipoMedida("Peso");
    setUnidadMedida("kg");
    setRendimiento("1");
    setMargen("0");
    setReceta([ingredienteVacio()]);
    setMaterialesReceta([]);
    setSeleccionesProveedor({});
    setPresentaciones(presentacionesDeTipo(presentacionesGenerales, "Peso"));
    setVersionCotizacion((actual) => actual + 1);
  };

  return (
    <div className="quotation-page">
      {error && <div className="alert alert-error">{error}</div>}
      <section className="panel quotation-builder">
        <div className="panel-title-row">
          <div>
            <h2>Nueva cotizacion</h2>
            <p>Configura la formula y el rendimiento del producto final.</p>
          </div>
          <div className="header-actions">
            <button className="btn" type="button" onClick={limpiarCotizacion}>
              <RotateCcw size={17} />
              Limpiar cotizacion
            </button>
            <button className="btn btn-primary" type="button" disabled={!puedeCrear || cargando} onClick={crearProducto}>
              <Package size={18} />
              Crear producto
            </button>
          </div>
        </div>

        <div className="quotation-header-grid">
          <Field label="Nombre del producto final *" value={nombre} onChange={(event) => setNombre(event.target.value)} />
          <Select label="Tipo de medida" value={tipoMedida} onChange={(event) => {
            const tipo = event.target.value;
            setTipoMedida(tipo);
            setUnidadMedida(baseDe(tipo));
            setPresentaciones(presentacionesDeTipo(presentacionesGenerales, tipo));
          }} options={["Peso", "Volumen"]} />
          <Field label="Cantidad producida *" type="text" inputMode="decimal" min="0" step="0.001" value={rendimiento} onChange={(event) => setRendimiento(event.target.value)} />
          <Select label="Unidad producida" value={unidadMedida} onChange={(event) => setUnidadMedida(event.target.value)} options={unidadesDe(tipoMedida)} />
          <Field label="Margen de ganancia (%)" type="text" inputMode="decimal" min="0" step="0.01" value={margen} onChange={(event) => setMargen(event.target.value)} />
        </div>

        <div className="section-title-actions quotation-section-title">
          <h3>Ingredientes</h3>
          <button className="btn" type="button" onClick={() => setReceta((actual) => [...actual, ingredienteVacio()])}>
            <Plus size={17} /> Agregar ingrediente
          </button>
        </div>
        <div className="ops-line-list">
          {receta.map((item, index) => (
            <div className="ops-line quotation-ingredient-line" key={index}>
              <SearchableSelect
                key={`${versionCotizacion}-ingrediente-${index}-${productos.length > 0}`}
                label="Materia prima o intermedio"
                value={item.productoId}
                onChange={(value) => actualizarIngrediente(index, "productoId", value)}
                options={ingredientesDisponibles.map((producto) => ({
                  value: producto._id,
                  label: `${producto.nombre} - ${producto.tipo}`,
                }))}
              />
              <Field label="Tipo" value={item.tipo || "-"} disabled />
              <Field label="Cantidad *" type="text" inputMode="decimal" min="0" step="0.001" value={item.cantidad} onChange={(event) => actualizarIngrediente(index, "cantidad", event.target.value)} />
              <Select label="Unidad" value={item.unidadMedida} onChange={(event) => actualizarIngrediente(index, "unidadMedida", event.target.value)} options={unidadesDe(item.tipoMedida)} />
              <button className="btn btn-danger action-icon-button quotation-remove" type="button" title="Quitar ingrediente" aria-label="Quitar ingrediente" onClick={() => setReceta((actual) => actual.filter((_, itemIndex) => itemIndex !== index))}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="section-title-actions quotation-section-title">
          <div>
            <h3>Materiales de produccion</h3>
            <p className="section-help">Opcional.</p>
          </div>
          <button className="btn" type="button" onClick={() => setMaterialesReceta((actual) => [...actual, materialVacio()])}>
            <Plus size={17} /> Agregar material
          </button>
        </div>
        {materialesReceta.length === 0 ? <div className="empty-inline">Sin materiales agregados.</div> : (
          <div className="ops-line-list">
            {materialesReceta.map((item, index) => (
              <div className="ops-line quotation-material-line" key={index}>
                <SearchableSelect
                  key={`${versionCotizacion}-material-${index}-${materiales.length > 0}`}
                  label="Material"
                  value={item.materialId}
                  onChange={(value) => actualizarMaterial(index, "materialId", value)}
                  options={materiales.map((material) => ({ value: material._id, label: material.nombre }))}
                />
                <Field label="Cantidad por produccion" type="text" inputMode="decimal" min="0" step="1" value={item.cantidad} onChange={(event) => actualizarMaterial(index, "cantidad", event.target.value)} />
                <button className="btn btn-danger action-icon-button quotation-remove" type="button" title="Quitar material" aria-label="Quitar material" onClick={() => setMaterialesReceta((actual) => actual.filter((_, itemIndex) => itemIndex !== index))}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="panel quotation-results">
        <div className="panel-title-row">
          <div>
            <h2>Costos calculados</h2>
            <p>Se selecciona inicialmente el proveedor con menor costo.</p>
          </div>
          <Calculator size={22} />
        </div>
        {cargando ? <div className="loading-state">Cargando costos...</div> : (
          <>
            {simulacion.errores.map((mensaje) => <div className="alert alert-error" key={mensaje}>{mensaje}</div>)}
            {simulacion.lineas.length === 0 ? <div className="empty-state">Agrega ingredientes para calcular la cotizacion.</div> : (
              <div className="table-wrap simulation-table quotation-cost-table">
                <table>
                  <thead><tr><th>Componente</th><th>Tipo</th><th>Cantidad</th><th>Proveedor</th><th>Costo unitario</th><th>Subtotal</th></tr></thead>
                  <tbody>{simulacion.lineas.map((linea) => (
                    <tr key={linea.clave}>
                      <td><strong>{linea.nombre}</strong></td>
                      <td>{linea.tipo === "material" ? "Material" : "Materia prima"}</td>
                      <td>{formatearCantidad(linea.cantidad)} {linea.unidad}</td>
                      <td>
                        <select className="simulation-provider-select" value={linea.seleccionada?.proveedor?._id || ""} onChange={(event) => setSeleccionesProveedor((actual) => ({ ...actual, [linea.clave]: event.target.value }))}>
                          {linea.opciones.length === 0 && <option value="">Sin proveedor</option>}
                          {linea.opciones.map((opcion) => <option key={opcion.proveedor._id} value={opcion.proveedor._id}>{opcion.proveedor.razonSocial} - {formatearImporte(opcion.precio)}</option>)}
                        </select>
                      </td>
                      <td>{formatearImporte(linea.seleccionada?.precio)} / {linea.unidad}</td>
                      <td><strong>{formatearImporte(linea.costoTotal)}</strong></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {simulacion.faltantes.length > 0 && <div className="simulation-warning">El total es parcial: hay componentes sin precio de proveedor.</div>}
            <div className="quotation-totals">
              <div><span>Ingredientes</span><strong>{formatearImporte(simulacion.costoIngredientes)}</strong></div>
              <div><span>Materiales</span><strong>{formatearImporte(simulacion.costoMateriales)}</strong></div>
              <div><span>Costo de la produccion</span><strong>{formatearImporte(simulacion.costoTotal)}</strong></div>
              <div><span>Costo por {baseDe(tipoMedida)}</span><strong>{formatearImporte(simulacion.costoPorUnidad)}</strong></div>
              <div className="quotation-sale-total"><span>Precio calculado de la produccion</span><strong>{formatearImporte(simulacion.precioVentaTotal)}</strong></div>
              <div className="quotation-sale-total"><span>Precio calculado por {baseDe(tipoMedida)}</span><strong>{formatearImporte(simulacion.precioVentaPorUnidad)}</strong></div>
            </div>
            <section className="quotation-presentations">
              <div className="section-title-actions">
                <div>
                  <h3>Precios por presentacion</h3>
                  <p className="section-help">Los precios se calculan con el margen indicado y pueden ajustarse manualmente.</p>
                </div>
                <button className="btn" type="button" onClick={() => setPresentaciones((actual) => [...actual, presentacionVacia(tipoMedida)])}>
                  <Plus size={17} /> Agregar presentacion
                </button>
              </div>
              {presentaciones.length === 0 ? <div className="empty-inline">No hay presentaciones. Agrega una para cotizar su precio de venta.</div> : (
                <div className="table-wrap">
                  <table className="product-presentations-table quotation-presentations-table">
                    <thead><tr><th>Origen</th><th>Cantidad</th><th>Unidad</th><th>Precio calculado</th><th>Precio de venta</th><th>Acciones</th></tr></thead>
                    <tbody>{presentaciones.map((item, index) => {
                      const precioSugerido = precioSugeridoPresentacion(item);
                      return (
                        <tr key={`${item.origenGeneral ? "general" : "especifica"}-${index}`}>
                          <td><span className={`badge ${item.origenGeneral ? "ok" : "warn"}`}>{item.origenGeneral ? "General" : "Especifica"}</span></td>
                          <td><input type="text" inputMode="decimal" min="0" step="0.001" value={item.cantidad} disabled={item.origenGeneral} onChange={(event) => actualizarPresentacion(index, "cantidad", event.target.value)} /></td>
                          <td><select value={item.unidadMedida} disabled={item.origenGeneral} onChange={(event) => actualizarPresentacion(index, "unidadMedida", event.target.value)}>{unidadesDe(tipoMedida).map((unidad) => <option key={unidad} value={unidad}>{unidad}</option>)}</select></td>
                          <td><strong>{formatearImporte(precioSugerido)}</strong></td>
                          <td><input type="text" inputMode="decimal" min="0" step="0.01" value={item.precioVenta === "" ? precioSugerido : item.precioVenta} onChange={(event) => actualizarPresentacion(index, "precioVenta", event.target.value)} /></td>
                          <td>
                            <div className="row-actions">
                              <button className="btn action-icon-button" type="button" title="Aplicar precio calculado" aria-label="Aplicar precio calculado" onClick={() => actualizarPresentacion(index, "precioVenta", precioSugerido)}><Calculator size={16} /></button>
                              {!item.origenGeneral && <button className="btn btn-danger action-icon-button" type="button" title="Quitar presentacion" aria-label="Quitar presentacion" onClick={() => setPresentaciones((actual) => actual.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={16} /></button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </section>
    </div>
  );
}

function Field({ label, ...props }) {
  return <div className="field"><label>{label}</label><input {...props} /></div>;
}

function Select({ label, options, ...props }) {
  return <div className="field"><label>{label}</label><select {...props}>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>;
}

function SearchableSelect({ label, value, options, onChange }) {
  const etiquetaSeleccionada = options.find((option) => String(option.value) === String(value || ""))?.label || "";
  const [texto, setTexto] = useState(etiquetaSeleccionada);
  const [abierto, setAbierto] = useState(false);
  const query = normalizar(texto);
  const filtradas = options.filter((option) => normalizar(option.label).includes(query)).slice(0, 20);

  return (
    <div className="field searchable-select">
      <label>{label}</label>
      <div className="search">
        <Search size={16} />
        <input type="search" value={texto} placeholder="Buscar..." autoComplete="off" onFocus={() => setAbierto(true)} onBlur={() => window.setTimeout(() => setAbierto(false), 120)} onChange={(event) => {
          setTexto(event.target.value);
          setAbierto(true);
          onChange("");
        }} />
      </div>
      {abierto && <div className="searchable-options">{filtradas.length === 0 ? <div className="searchable-empty">Sin resultados</div> : filtradas.map((option) => <button key={option.value} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => {
        setTexto(option.label);
        setAbierto(false);
        onChange(option.value);
      }}>{option.label}</button>)}</div>}
    </div>
  );
}
